import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Eye, LifeBuoy, Send } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import { ContactAdminImagePicker } from '../components/contactAdmin/ContactAdminImages';
import {
  NEW_TICKET_MAX,
  NEW_TICKET_MIN,
  STATUS_META,
  announceContactAdminChange,
  apiError,
  ticketPath,
} from '../components/contactAdmin/contactAdmin';
import {
  createSupportTicket,
  fetchSupportCategories,
  fetchSupportTickets,
  type SupportCategory,
  type SupportTicketSummary,
} from '../services/api';

/**
 * Contact admin (launch item A7): the Shop writes to the Vault Play admin team and follows its
 * tickets here. The list also holds tickets the admin team shared with the Shop to read.
 */
export default function ContactAdminPage() {
  const navigate = useNavigate();
  const { timezone } = useTimezone();
  const [categories, setCategories] = useState<SupportCategory[]>([]);
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryId, setCategoryId] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const onImages = useCallback((ids: string[], busy: boolean) => {
    setAttachmentIds(ids);
    setUploading(busy);
  }, []);

  const load = useCallback(async () => {
    try {
      const [cats, list] = await Promise.all([fetchSupportCategories(), fetchSupportTickets({ page: 1, limit: 50 })]);
      if (cats.success && cats.data) setCategories(cats.data.categories);
      if (list.success && list.data) setTickets(list.data.tickets);
    } catch {
      toast.error('Could not load your tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (!categoryId) found.category_id = 'Choose a topic.';
    if (message.trim().length < NEW_TICKET_MIN) found.message = `Write at least ${NEW_TICKET_MIN} characters.`;
    setErrors(found);
    if (Object.keys(found).length) return;

    setSending(true);
    try {
      const res = await createSupportTicket({ categoryId, message: message.trim(), attachmentIds });
      if (res.success && res.data) {
        toast.success('Sent to the admin team');
        announceContactAdminChange();
        navigate(ticketPath(res.data.ticketNumber));
        return;
      }
      toast.error('Could not send your message');
    } catch (error) {
      const { message: text, errors: fieldErrors } = apiError(error, 'Could not send your message');
      setErrors(fieldErrors);
      toast.error(text);
    } finally {
      setSending(false);
    }
  };

  return (
    <AppLayout>
      <div className="mb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-stone-900">
          <LifeBuoy className="h-5 w-5 text-emerald-600" aria-hidden /> Contact admin
        </h1>
        <p className="mt-1 text-sm text-stone-600">
          Write to the Vault Play admin team. Their replies show here, and by email when your Shop has one.
        </p>
      </div>

      <form onSubmit={submit} noValidate className="mb-6 grid gap-3 rounded-lg border border-stone-200 bg-white p-4" aria-label="New message to the admin team">
        <h2 className="text-sm font-semibold text-stone-900">New message</h2>
        <div className="grid gap-1">
          <label htmlFor="contact-admin-topic" className="text-sm font-medium text-stone-700">
            Topic
          </label>
          <select
            id="contact-admin-topic"
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setErrors((prev) => ({ ...prev, category_id: '' }));
            }}
            aria-invalid={Boolean(errors.category_id) || undefined}
            className="h-10 rounded-lg border border-stone-300 bg-white px-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          >
            <option value="">Choose a topic</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {errors.category_id ? <p className="text-xs text-red-700">{errors.category_id}</p> : null}
        </div>
        <div className="grid gap-1">
          <label htmlFor="contact-admin-message" className="text-sm font-medium text-stone-700">
            Message
          </label>
          <textarea
            id="contact-admin-message"
            rows={5}
            maxLength={NEW_TICKET_MAX}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setErrors((prev) => ({ ...prev, message: '' }));
            }}
            aria-invalid={Boolean(errors.message) || undefined}
            placeholder="What do you need help with?"
            className="rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          />
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-red-700">{errors.message || ''}</span>
            <span className="text-stone-500">
              {message.length} / {NEW_TICKET_MAX}
            </span>
          </div>
        </div>
        <ContactAdminImagePicker onChange={onImages} disabled={sending} />
        <button
          type="submit"
          disabled={sending || uploading}
          className="inline-flex items-center gap-2 justify-self-start rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          <Send className="h-4 w-4" aria-hidden />
          {sending ? 'Sending…' : 'Send to the admin team'}
        </button>
      </form>

      <h2 className="mb-2 text-sm font-semibold text-stone-900">Your tickets</h2>
      {loading ? (
        <PageLoadingIndicator />
      ) : tickets.length === 0 ? (
        <p className="text-sm text-stone-500">No tickets yet. Messages you send to the admin team show here.</p>
      ) : (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white" aria-label="Your tickets">
          {tickets.map((t) => (
            <li key={t.ticketNumber}>
              <Link to={ticketPath(t.ticketNumber)} className="flex items-start gap-3 px-4 py-3 hover:bg-stone-50">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-mono text-xs font-semibold text-stone-800">#{t.ticketNumber}</span>
                    <span className="font-medium text-stone-900">{t.categoryDisplayName}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_META[t.status]?.className ?? ''}`}>
                      {STATUS_META[t.status]?.label ?? t.status}
                    </span>
                    {t.access === 'shared' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-800 ring-1 ring-inset ring-violet-200">
                        <Eye className="h-3 w-3" aria-hidden /> Shared by the admin team
                      </span>
                    ) : null}
                    {t.hasUnread ? (
                      <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">New reply</span>
                    ) : null}
                  </div>
                  <p className="mt-1 truncate text-sm text-stone-600">{t.messagePreview}</p>
                </div>
                <span className="shrink-0 text-xs text-stone-500">{formatInTimezone(t.lastActivityAt || t.createdAt, timezone)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </AppLayout>
  );
}
